import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CameraIcon, FolderIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/Button";
import { formatDate } from "@/lib/format";
import {
  getProjectFotoDisplayUrl,
  listProjectFotos,
  projectFotoFolderLabel,
  projectFotoIsImage,
  resolveProjectFotoPreviewUrl,
  type ProjectFoto,
  type ProjectFotoSession,
} from "@/lib/projectOverview";
import {
  ProjectFotoLightbox,
  projectFotoLightboxState,
  type ProjectFotoLightboxState,
} from "@/projectOverview/ProjectFotoLightbox";
import { ProjectFotosDialog } from "@/projectOverview/ProjectFotosDialog";
import { OverviewCard } from "@/projectOverview/OverviewCard";
import {
  filesForProjectFotos,
  projectFotoUploadErrorMessage,
  uploadDroppedProjectFotos,
} from "@/projectOverview/uploadDroppedProjectFotos";
import { toast } from "@/lib/toast";

const MAX_VISIBLE_FOLDERS = 4;
const MOSAIC_SIZE = 4;

function isImageFoto(foto: ProjectFoto): boolean {
  return projectFotoIsImage(foto);
}

function sessionPhotoCount(session: ProjectFotoSession): number {
  return session.photos?.length ?? session.photo_count ?? 0;
}

function sessionImages(session: ProjectFotoSession): ProjectFoto[] {
  return (session.photos || []).filter(isImageFoto);
}

function sessionWhen(session: ProjectFotoSession): string {
  const fromSession = formatDate(session.created_at);
  if (fromSession !== "—") return fromSession;
  const first = session.photos?.[0]?.uploaded_at;
  return formatDate(first);
}

export function ProjectFotosSection({ orderId }: { orderId: number }) {
  const [sessions, setSessions] = useState<ProjectFotoSession[] | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [startInUpload, setStartInUpload] = useState(false);
  const [lightbox, setLightbox] = useState<ProjectFotoLightboxState | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const cardRef = useRef<HTMLElement>(null);
  const sessionsRef = useRef(sessions);
  sessionsRef.current = sessions;

  const reload = useCallback(async () => {
    try {
      const list = await listProjectFotos(orderId);
      setSessions(Array.isArray(list) ? list : []);
    } catch {
      setSessions([]);
    }
  }, [orderId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!dialogOpen) void reload();
  }, [dialogOpen, reload]);

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!files.length || uploading) return;
      if (sessionsRef.current === null) {
        toast.error("Fotos werden noch geladen.");
        return;
      }
      setUploading(true);
      try {
        const result = await uploadDroppedProjectFotos(
          orderId,
          files,
          sessionsRef.current ?? [],
        );
        if (result.count > 0) {
          toast.success(
            result.count === 1
              ? `Foto in „${result.folderTitle}“ abgelegt.`
              : `${result.count} Fotos in „${result.folderTitle}“ abgelegt.`,
          );
        }
        await reload();
      } catch (err) {
        toast.error(projectFotoUploadErrorMessage(err));
        await reload();
      } finally {
        setUploading(false);
      }
    },
    [orderId, reload, uploading],
  );

  useEffect(() => {
    const root = cardRef.current;
    if (!root) return;
    const dropRoot: HTMLElement = root;
    const desktop = window.matchMedia("(min-width: 768px)");
    if (!desktop.matches) return;

    dropRoot.setAttribute("data-project-foto-drop", String(orderId));

    function isFileDrag(event: DragEvent): boolean {
      const types = event.dataTransfer?.types;
      if (!types) return false;
      return Array.from(types).includes("Files");
    }

    function setRootActive(active: boolean) {
      dropRoot.classList.toggle("is-foto-drop", active);
      setDragOver(active);
    }

    function onDragOver(event: DragEvent) {
      if (!isFileDrag(event)) return;
      event.preventDefault();
    }

    function onDragEnter(event: DragEvent) {
      if (!isFileDrag(event)) return;
      const previous = event.relatedTarget;
      if (previous instanceof Node && dropRoot.contains(previous)) return;
      setRootActive(true);
    }

    function onDragLeave(event: DragEvent) {
      const next = event.relatedTarget;
      if (next instanceof Node && dropRoot.contains(next)) return;
      setRootActive(false);
    }

    function onDrop(event: DragEvent) {
      const accepted = filesForProjectFotos(event.dataTransfer?.files);
      setRootActive(false);
      if (!accepted.length) return;
      event.preventDefault();
      event.stopPropagation();
      void uploadFiles(accepted);
    }

    dropRoot.addEventListener("dragover", onDragOver);
    dropRoot.addEventListener("dragenter", onDragEnter);
    dropRoot.addEventListener("dragleave", onDragLeave);
    dropRoot.addEventListener("drop", onDrop);
    return () => {
      setDragOver(false);
      dropRoot.classList.remove("is-foto-drop");
      dropRoot.removeAttribute("data-project-foto-drop");
      dropRoot.removeEventListener("dragover", onDragOver);
      dropRoot.removeEventListener("dragenter", onDragEnter);
      dropRoot.removeEventListener("dragleave", onDragLeave);
      dropRoot.removeEventListener("drop", onDrop);
    };
  }, [orderId, uploadFiles]);

  const folders = sessions ?? [];
  const totalPhotos = useMemo(
    () => (sessions ?? []).reduce((sum, s) => sum + sessionPhotoCount(s), 0),
    [sessions],
  );
  const visibleFolders = folders.slice(0, MAX_VISIBLE_FOLDERS);
  const hiddenFolderCount = Math.max(0, folders.length - visibleFolders.length);
  const loading = sessions === null;

  function openDialog(upload: boolean) {
    setStartInUpload(upload);
    setDialogOpen(true);
  }

  function openPreview(foto: ProjectFoto, session: ProjectFotoSession) {
    setLightbox(projectFotoLightboxState(orderId, session.photos || [], foto));
  }

  const summary =
    totalPhotos > 0
      ? `${totalPhotos} Foto${totalPhotos === 1 ? "" : "s"} · ${folders.length} Ordner`
      : folders.length > 0
        ? `${folders.length} Ordner · noch leer`
        : null;

  return (
    <>
      <OverviewCard
        title="Projektfotos"
        sectionRef={cardRef}
        className={dragOver ? "is-foto-drop-target" : undefined}
      >
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-label-secondary">
            {uploading
              ? "Fotos werden hochgeladen …"
              : dragOver
                ? "Fotos hier ablegen"
                : loading
                  ? "Ordner werden geladen …"
                  : summary || "Noch keine Fotos vorhanden."}
          </p>
          <Button
            type="button"
            size="sm"
            variant={totalPhotos > 0 ? "secondary" : "primary"}
            leftIcon={<PlusIcon className="h-4 w-4" />}
            onClick={() => openDialog(true)}
          >
            Hochladen
          </Button>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 gap-2">
            <div className="h-28 animate-pulse rounded-ios-lg bg-fill" />
            <div className="h-28 animate-pulse rounded-ios-lg bg-fill" />
          </div>
        ) : folders.length === 0 ? (
          <button
            type="button"
            onClick={() => openDialog(true)}
            className="eh-po-foto-empty"
          >
            <span className="eh-po-foto-empty-icon" aria-hidden>
              <CameraIcon className="h-6 w-6" />
            </span>
            <span className="text-sm font-medium text-label">Fotos vom Einsatz ablegen</span>
            <span className="hidden text-xs text-label-tertiary sm:block">oder Dateien hierher ziehen</span>
          </button>
        ) : (
          <div className="space-y-2">
            <ul className={visibleFolders.length === 1 ? "grid grid-cols-1 gap-2" : "grid grid-cols-1 gap-2 sm:grid-cols-2"}>
              {visibleFolders.map((session) => (
                <li key={session.id}>
                  <FolderPreviewCard
                    session={session}
                    orderId={orderId}
                    compact={visibleFolders.length > 1}
                    onOpen={() => openDialog(false)}
                    onPreview={(foto) => openPreview(foto, session)}
                  />
                </li>
              ))}
            </ul>
            {hiddenFolderCount > 0 ? (
              <button type="button" className="eh-po-foto-more" onClick={() => openDialog(false)}>
                {hiddenFolderCount} weitere Ordner anzeigen
              </button>
            ) : null}
          </div>
        )}
      </OverviewCard>

      <ProjectFotosDialog
        open={dialogOpen}
        orderId={orderId}
        initialUploadMode={startInUpload}
        onClose={() => setDialogOpen(false)}
        onChanged={() => void reload()}
      />

      <ProjectFotoLightbox
        state={lightbox}
        onClose={() => setLightbox(null)}
        onIndexChange={(index) =>
          setLightbox((prev) => (prev ? { ...prev, index } : prev))
        }
      />
    </>
  );
}

function FolderPreviewCard({
  session,
  orderId,
  compact,
  onOpen,
  onPreview,
}: {
  session: ProjectFotoSession;
  orderId: number;
  compact: boolean;
  onOpen: () => void;
  onPreview: (foto: ProjectFoto) => void;
}) {
  const images = sessionImages(session);
  const previews = images.slice(0, MOSAIC_SIZE);
  const extra = Math.max(0, images.length - previews.length);
  const useGrid = previews.length > 1;
  const count = sessionPhotoCount(session);
  const when = sessionWhen(session);
  const folderLabel = projectFotoFolderLabel(session.folder_segment);
  const meta = [
    `${count} Foto${count === 1 ? "" : "s"}`,
    when !== "—" ? when : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <article className="eh-po-foto-folder">
      <div
        className={
          useGrid
            ? "eh-po-foto-mosaic eh-po-foto-mosaic--grid"
            : "eh-po-foto-mosaic eh-po-foto-mosaic--hero"
        }
      >
        {previews.length === 0 ? (
          <button type="button" className="eh-po-foto-placeholder" onClick={onOpen}>
            <FolderIcon className="h-6 w-6" />
            <span>Noch leer</span>
          </button>
        ) : (
          Array.from({ length: useGrid ? MOSAIC_SIZE : 1 }, (_, index) => {
            const foto = previews[index];
            if (!foto) {
              return (
                <span key={`empty-${index}`} className="eh-po-foto-placeholder eh-po-foto-placeholder--cell">
                  <CameraIcon className="h-4 w-4" />
                </span>
              );
            }
            return (
              <FotoThumb
                key={foto.id}
                orderId={orderId}
                foto={foto}
                extra={index === previews.length - 1 ? extra : 0}
                onPreview={() => onPreview(foto)}
              />
            );
          })
        )}
      </div>
      <button type="button" className="eh-po-foto-folder-meta" onClick={onOpen}>
        <span className="eh-po-foto-folder-title">{session.title || "Projektfotos"}</span>
        <span className="eh-po-foto-folder-sub">
          {meta}
          {folderLabel && !compact ? ` · ${folderLabel}` : ""}
        </span>
      </button>
    </article>
  );
}

function FotoThumb({
  orderId,
  foto,
  extra,
  onPreview,
}: {
  orderId: number;
  foto: ProjectFoto;
  extra: number;
  onPreview: () => void;
}) {
  const [broken, setBroken] = useState(false);
  const thumbUrl = resolveProjectFotoPreviewUrl(orderId, foto);
  const displayUrl = getProjectFotoDisplayUrl(orderId, foto.id);
  const [src, setSrc] = useState(thumbUrl);

  useEffect(() => {
    setSrc(thumbUrl);
    setBroken(false);
  }, [thumbUrl]);

  return (
    <button type="button" className="eh-po-foto-thumb" onClick={onPreview} title={foto.filename}>
      {broken ? (
        <span className="eh-po-foto-placeholder eh-po-foto-placeholder--cell">
          <CameraIcon className="h-4 w-4" />
        </span>
      ) : (
        <img
          src={src}
          alt={foto.original_filename || foto.filename}
          loading="lazy"
          decoding="async"
          crossOrigin="use-credentials"
          className="h-full w-full object-cover"
          onError={() => {
            if (src !== displayUrl) setSrc(displayUrl);
            else setBroken(true);
          }}
        />
      )}
      {extra > 0 ? <span className="eh-po-foto-extra">+{extra}</span> : null}
    </button>
  );
}
